import { ScatterplotLayer, LineLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { LayerState, LayerFeature, FerryProps } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { createGlideTracker, type GlideSample } from './glideTracker.js';
import { lerp } from './entityGlide.js';
import { createCometMotion } from './cometMotion.js';
import { pathCumulative, projectDistance } from './alongPath.js';
import { wakeAlongPath } from './ferryWake.js';
import {
  FERRY_GLIDE_MS,
  FERRY_SNAP_METERS,
  FERRY_JITTER_METERS,
  FERRY_MOVING_MPS,
  FERRY_MOVING_METERS,
  FERRY_WAKE_MS,
  FERRY_WAKE_SEGMENTS,
  FERRY_WAKE_ALPHA_NEAR,
  FERRY_WAKE_ALPHA_FAR,
  FERRY_CATCHUP_MPS,
} from './ferryMotion.js';

const props = (f: LayerFeature) => f.props as unknown as FerryProps;

interface FerryDatum {
  position: [number, number];
  fillColor: [number, number, number, number];
  lineColor: [number, number, number, number];
  radius: number;
  // Carried through so the hover tooltip can read FerryProps off `object.props`.
  props: FerryProps;
}

interface WakeDatum {
  source: [number, number];
  target: [number, number];
  color: [number, number, number, number];
  width: number;
}

// One tracker instance owns the per-ferry glide state across frames (no globals).
const tracker = createGlideTracker({
  glideMs: FERRY_GLIDE_MS,
  snapMeters: FERRY_SNAP_METERS,
  jitterMeters: FERRY_JITTER_METERS,
});

// One motion instance owns the per-ferry along-path state across frames (no globals).
const motion = createCometMotion({ catchupMps: FERRY_CATCHUP_MPS });

/** Underway boats with a resolved route window animate along their path;
 * everything else (docked, unmatched trip, degraded server) glides as a Point. */
const isPathFeature = (f: LayerFeature): boolean =>
  f.geometry.type === 'LineString' && Array.isArray(props(f).timestamps);

const FERRY_RGB: [number, number, number] = [56, 189, 248]; // cyan, matches normalizer
const FERRY_RADIUS = 7; // px
const FERRY_FILL_A = 235;
const FERRY_LINE: [number, number, number, number] = [255, 255, 255, 230];
const WAKE_FRAC = FERRY_WAKE_MS / FERRY_GLIDE_MS; // travel-fraction the wake trails behind

/**
 * Build the ferry layers: a wake-trail LineLayer under a ScatterplotLayer of ferry
 * positions. Underway boats with a resolved route window are LineString features
 * (`props.timestamps` set) that animate along their route path at schedule pace via
 * the comet motion instance, trailing a wake built along the route curve itself.
 * Everything else — docked, unmatched trip, or a degraded server that couldn't build
 * a window — arrives as a Point and keeps the id-keyed glide tracker (entity
 * interpolation) exactly as before, so those boats still roll continuously between
 * ~45s polls instead of freezing, with a glide-chord wake while underway. All boats
 * render with identical styling regardless of mode. Returns null when state is absent
 * or the feed is down with no features.
 */
export function buildFerryLayer(state: LayerState, nowSec: number): Layer[] | null {
  if (!state || state.status === 'down' || state.features.length === 0) return null;

  const pathFeatures = state.features.filter(isPathFeature);
  const pointFeatures = state.features.filter((f) => !isPathFeature(f));

  const dots: FerryDatum[] = [];
  const wakes: WakeDatum[] = [];

  // Path boats: schedule-paced position on the route curve, wake along the curve.
  if (pathFeatures.length > 0) {
    const samples = new Map(
      motion.step({ ...state, features: pathFeatures }, nowSec).map((s) => [s.id, s.position]),
    );
    for (const f of pathFeatures) {
      const p = props(f);
      const position = samples.get(f.id);
      if (!position) continue;
      dots.push({
        position,
        fillColor: [...FERRY_RGB, FERRY_FILL_A],
        lineColor: FERRY_LINE,
        radius: FERRY_RADIUS,
        props: p,
      });
      const path = (f.geometry as { coordinates: [number, number][] }).coordinates;
      const cum = pathCumulative(path);
      const boatDist = projectDistance(path, cum, position);
      for (const w of wakeAlongPath(path, cum, boatDist, p.speedMps ?? 0)) {
        wakes.push({ source: w.source, target: w.target, color: [...FERRY_RGB, w.alpha], width: w.width });
      }
    }
  }

  // Point boats (docked / unmatched trips): id-keyed glide, exactly as before.
  const nowMs = typeof performance !== 'undefined' ? performance.now() : 0;
  const glideSamples = tracker.step(
    pointFeatures.map((f) => ({
      id: f.id,
      position: (f.geometry as { coordinates: [number, number] }).coordinates,
      // Previous reported position from the server, so a just-connected client seeds
      // its glide and the boat moves on the first frame instead of waiting two polls.
      seedFrom: props(f).prev,
    })),
    state.generatedAt,
    nowMs,
  );
  for (const f of pointFeatures) {
    const s = glideSamples.get(f.id)!;
    const p = props(f);
    // Prefer the feed's reported speed (instant, correct on the first frame); fall
    // back to inferred per-poll movement only when the feed omits speed. Used only to
    // decide whether a boat trails a wake — all ferries render with identical styling.
    const underway =
      p.speedMps != null ? p.speedMps > FERRY_MOVING_MPS : s.movedMeters > FERRY_MOVING_METERS;
    dots.push({
      position: s.position,
      fillColor: [...FERRY_RGB, FERRY_FILL_A],
      lineColor: FERRY_LINE,
      radius: FERRY_RADIUS,
      props: p,
    });
    if (underway) wakes.push(...wakeSegments(s, nowMs));
  }

  const wakeLayer = new LineLayer<WakeDatum>({
    id: 'ferry-wake',
    data: wakes,
    getSourcePosition: (d) => d.source,
    getTargetPosition: (d) => d.target,
    getColor: (d) => d.color,
    getWidth: (d) => d.width,
    widthUnits: 'pixels',
    widthMinPixels: 1,
    pickable: false,
  });

  const dotLayer = new ScatterplotLayer<FerryDatum>({
    id: 'ferry',
    data: dots,
    getPosition: (d) => d.position,
    getFillColor: (d) => d.fillColor,
    getLineColor: (d) => d.lineColor,
    getRadius: (d) => d.radius,
    radiusUnits: 'pixels',
    radiusMinPixels: 3,
    radiusMaxPixels: 16,
    stroked: true,
    lineWidthMinPixels: 1.2,
    pickable: true,
    updateTriggers: {
      // Color/radius depend on the underway flag, which changes between polls.
      getFillColor: state.generatedAt,
      getLineColor: state.generatedAt,
      getRadius: state.generatedAt,
    },
  });

  return [wakeLayer, dotLayer];
}

/**
 * Build the fading wake behind one underway ferry: a few short segments along the
 * tail of its active glide (where it recently was), with alpha ramping up toward the
 * boat. The glide chord approximates the recent track; the wake covers the last
 * WAKE_FRAC of it.
 */
function wakeSegments(s: GlideSample, nowMs: number): WakeDatum[] {
  const { from, to, startMs, durationMs } = s.glide;
  // Settled glide (first sight / snap) spans no distance → no track to trail yet.
  if (from[0] === to[0] && from[1] === to[1]) return [];
  const head = durationMs <= 0 ? 1 : (nowMs - startMs) / durationMs;
  const p = head <= 0 ? 0 : head >= 1 ? 1 : head;
  const tail = Math.max(0, p - WAKE_FRAC);
  if (p - tail < 1e-4) return []; // nothing behind the boat yet (just left the dock)

  const segs: WakeDatum[] = [];
  const span = p - tail;
  for (let k = 0; k < FERRY_WAKE_SEGMENTS; k++) {
    const f0 = tail + (span * k) / FERRY_WAKE_SEGMENTS;
    const f1 = tail + (span * (k + 1)) / FERRY_WAKE_SEGMENTS;
    // k+1 is closer to the boat → stronger alpha and wider.
    const t = (k + 1) / FERRY_WAKE_SEGMENTS;
    const alpha = Math.round(FERRY_WAKE_ALPHA_FAR + (FERRY_WAKE_ALPHA_NEAR - FERRY_WAKE_ALPHA_FAR) * t);
    segs.push({
      source: lerp(from, to, f0),
      target: lerp(from, to, f1),
      color: [...FERRY_RGB, alpha],
      width: 1 + 2 * t,
    });
  }
  return segs;
}

export const ferryRenderer: RendererDef = {
  layerId: 'ferry',
  build: (state, nowSec) => buildFerryLayer(state, nowSec),
};
